import { useEffect, useMemo, useState, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { format, subDays, parseISO } from "date-fns";
import {
  RefreshCw,
  MousePointerClick,
  DollarSign,
  Globe2,
  Layers,
  Search as SearchIcon,
  ShoppingBag,
  ExternalLink,
  Calendar as CalendarIcon,
  Terminal,
  Link2,
  Copy,
  Check,
  Wrench,
  Info,
  Store,
  Package,
  Webhook,
  AlertTriangle,
  BarChart3,
  Tag,
  ChevronRight,
  Users as UsersIcon,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { Toaster, toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AuthProvider, useAuth, LoginPage, UserMenu, UsersPage } from "@/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RANGE_PRESETS = [
  { label: "7d", days: 7 },
  { label: "14d", days: 14 },
  { label: "30d", days: 30 },
];

const COUNTRY_OPTIONS = [
  { code: "us", label: "United States" }, { code: "ca", label: "Canada" },
  { code: "mx", label: "Mexico" }, { code: "br", label: "Brazil" },
  { code: "gb", label: "United Kingdom" }, { code: "de", label: "Germany" },
  { code: "fr", label: "France" }, { code: "es", label: "Spain" },
  { code: "it", label: "Italy" }, { code: "nl", label: "Netherlands" },
  { code: "pl", label: "Poland" }, { code: "se", label: "Sweden" },
  { code: "be", label: "Belgium" }, { code: "sa", label: "Saudi Arabia" },
  { code: "ae", label: "UAE" }, { code: "jp", label: "Japan" },
  { code: "au", label: "Australia" }, { code: "sg", label: "Singapore" },
];

const AMAZON_MARKETPLACES = [
  { code: "us", host: "www.amazon.com" }, { code: "ca", host: "www.amazon.ca" },
  { code: "mx", host: "www.amazon.com.mx" }, { code: "br", host: "www.amazon.com.br" },
  { code: "gb", host: "www.amazon.co.uk" }, { code: "de", host: "www.amazon.de" },
  { code: "fr", host: "www.amazon.fr" }, { code: "es", host: "www.amazon.es" },
  { code: "it", host: "www.amazon.it" }, { code: "nl", host: "www.amazon.nl" },
  { code: "pl", host: "www.amazon.pl" }, { code: "se", host: "www.amazon.se" },
  { code: "jp", host: "www.amazon.co.jp" }, { code: "au", host: "www.amazon.com.au" },
  { code: "sg", host: "www.amazon.sg" }, { code: "ae", host: "www.amazon.ae" },
  { code: "sa", host: "www.amazon.sa" },
];

// Enabled by Shopnomix on this campaign right now (per Anthing — Apr 2026).
// Other geos require Shopnomix to enable based on volume.
const ENABLED_AMZ_GEOS = new Set(["gb", "de", "fr"]);

const isAmazon = (d) => !!d && /amazon\./i.test(d);
const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const fmtInt = (v) => Number(v || 0).toLocaleString();
const fmtDate = (iso) => { if (!iso) return "—"; try { return format(parseISO(iso), "MMM d"); } catch { return iso; } };
const fmtDateTime = (s) => {
  if (!s) return "—";
  try {
    const d = typeof s === "string" ? new Date(s.replace(" ", "T")) : new Date(s);
    return format(d, "yyyy-MM-dd HH:mm");
  } catch { return String(s); }
};
const todayStr = () => format(new Date(), "yyyy-MM-dd");

// ---------- Reusable bits ----------

function Stat({ icon: Icon, label, value, sub, testId, accent }) {
  const ring = accent === "amazon" ? "border-amber-200 bg-amber-50 text-amber-700" : accent === "brand" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-zinc-200 bg-zinc-50 text-zinc-700";
  return (
    <Card data-testid={testId} className="border-zinc-200/70">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
            <div className="mt-3 font-mono text-3xl font-semibold tabular-nums text-zinc-900">{value}</div>
            {sub ? <div className="mt-1 text-xs text-zinc-500">{sub}</div> : null}
          </div>
          <div className={`rounded-md border p-2 ${ring}`}><Icon className="h-4 w-4" /></div>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ title, hint }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-zinc-200 bg-zinc-50/40 px-6 py-14 text-center">
      <div className="text-sm font-medium text-zinc-700">{title}</div>
      {hint ? <div className="mt-1 max-w-md text-xs text-zinc-500">{hint}</div> : null}
    </div>
  );
}

function SimpleTable({ columns, rows, empty, testId }) {
  if (!rows || rows.length === 0) return <EmptyState title={empty.title} hint={empty.hint} />;
  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200" data-testid={testId}>
      <Table>
        <TableHeader>
          <TableRow className="bg-zinc-50">
            {columns.map((c) => (
              <TableHead key={c.key} className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">{c.label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} className="hover:bg-zinc-50/60">
              {columns.map((c) => (
                <TableCell key={c.key} className={c.mono ? "font-mono tabular-nums text-sm" : "text-sm"}>
                  {c.render ? c.render(r) : r[c.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function CopyableLink({ link, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };
  return (
    <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 p-3">
      <code className="flex-1 break-all text-xs text-zinc-800" data-testid={testId}>{link}</code>
      <Button size="sm" variant="outline" onClick={copy}>
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}

function ConversionsTable({ rows, scope }) {
  const [status, setStatus] = useState("ALL");
  const counts = useMemo(() => {
    const c = { CONFIRMED: 0, OPEN: 0, REJECTED: 0 };
    rows.forEach((x) => { if (c[x.status] !== undefined) c[x.status] += 1; });
    return c;
  }, [rows]);
  const filtered = useMemo(() => status === "ALL" ? rows : rows.filter((r) => r.status === status), [rows, status]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {[{ key: "ALL", label: `All · ${rows.length}` }, { key: "CONFIRMED", label: `Confirmed · ${counts.CONFIRMED}` }, { key: "OPEN", label: `Open · ${counts.OPEN}` }, { key: "REJECTED", label: `Rejected · ${counts.REJECTED}` }].map((s) => (
          <Button key={s.key} size="sm" variant={status === s.key ? "default" : "outline"} onClick={() => setStatus(s.key)} data-testid={`${scope}-conv-filter-${s.key.toLowerCase()}`} className="h-7 text-xs">{s.label}</Button>
        ))}
      </div>
      <SimpleTable
        testId={`${scope}-conversions-table`}
        columns={[
          { key: "click_time", label: "Click time", mono: true, render: (r) => fmtDateTime(r.click_time) },
          { key: "country", label: "Country", render: (r) => <span className="uppercase">{r.country || "—"}</span> },
          { key: "root_domain", label: "Domain" },
          { key: "source", label: "Source", render: (r) => r.source || <span className="text-zinc-400">—</span> },
          { key: "status", label: "Status", render: (r) => <Badge variant={r.status === "CONFIRMED" ? "default" : r.status === "REJECTED" ? "destructive" : "secondary"}>{r.status}</Badge> },
          { key: "revenue", label: "Commission", mono: true, render: (r) => fmtMoney(r.revenue) },
        ]}
        rows={filtered.slice(0, 200)}
        empty={{ title: "No conversions in this view", hint: status === "ALL" ? "Clicks may take time to convert. Some merchants report instantly, others batch hourly or daily." : `No ${status.toLowerCase()} conversions in this window.` }}
      />
    </div>
  );
}

function PageHeader({ title, description, badge }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b border-zinc-200 pb-4">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-900">{title}</h2>
          {badge ? <Badge variant="secondary" className="text-[10px]">{badge}</Badge> : null}
        </div>
        {description ? <p className="mt-1 max-w-2xl text-sm text-zinc-500">{description}</p> : null}
      </div>
    </div>
  );
}

// ---------- Pages ----------

function OverviewPage({ totals, conversions, byDate, campaign }) {
  const uniqueCountries = useMemo(() => new Set(conversions.map((c) => c.country).filter(Boolean)).size, [conversions]);
  const chartData = useMemo(() => (byDate || []).map((d) => ({
    date: d.date ? format(new Date(d.date), "MMM d") : "—",
    clicks: Number(d.clicks || 0), revenue: Number(d.revenue || 0),
  })).reverse(), [byDate]);

  return (
    <>
      <PageHeader
        title="Overview"
        description={`Source of truth for "${campaign.campaign_name || "this campaign"}" — totals, daily clicks/revenue, and a quick split between Amazon and other brands.`}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={MousePointerClick} label="Total clicks" value={fmtInt(totals.total_clicks)} sub={`${totals.days_with_data || 0} days reported`} testId="stat-clicks" />
        <Stat icon={DollarSign} label="Total commission" value={fmtMoney(totals.total_revenue)} sub="USD across Amazon + brands" testId="stat-revenue" />
        <Stat icon={Layers} label="Conversions" value={fmtInt(conversions.length)} sub={conversions.length ? `${conversions.filter((c) => c.status === "CONFIRMED").length} confirmed` : "—"} testId="stat-conversions" />
        <Stat icon={Globe2} label="Countries" value={fmtInt(uniqueCountries)} sub={uniqueCountries ? "with conversions" : "no conversion data"} testId="stat-countries" />
      </div>

      <Card className="mt-6 border-zinc-200/70">
        <CardHeader>
          <CardTitle className="text-base">Clicks &amp; commission · daily</CardTitle>
          <CardDescription>Stats refresh hourly · per-merchant cadence varies (instant → daily batch)</CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <EmptyState title="No data yet for this range" hint="If you've sent clicks recently, give it up to an hour to surface here." />
          ) : (
            <div className="h-72 w-full" data-testid="reports-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#f1f1f1" vertical={false} />
                  <XAxis dataKey="date" stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis yAxisId="l" stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis yAxisId="r" orientation="right" stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 }} formatter={(v, n) => (n === "revenue" ? fmtMoney(v) : fmtInt(v))} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="l" type="monotone" dataKey="clicks" stroke="#0f172a" strokeWidth={2} dot={{ r: 2 }} />
                  <Line yAxisId="r" type="monotone" dataKey="revenue" stroke="#dc2626" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function AmzHowToPage() {
  return (
    <>
      <PageHeader title="How Amazon works" description="Why Amazon isn't in the brand catalog and how to monetize it on this campaign." badge="Amazon" />
      <Card className="border-amber-200 bg-amber-50/40">
        <CardContent className="space-y-3 py-6 text-sm text-amber-900">
          <p>Amazon is <span className="font-semibold">not</span> in the Demand Feed — Shopnomix excludes it because it uses your <span className="font-semibold">Amazon Associates partner tag</span>, injected automatically server-side. So Amazon traffic is monetized through the <span className="font-mono">/api/v1/bid/redirect</span> URL, not via brand bidding.</p>
          <ol className="list-decimal space-y-1 pl-5">
            <li>Pick the marketplace (US, DE, UK, JP, …) — each has its own associate tag on Shopnomix's side.</li>
            <li>Use <span className="font-medium">Amazon → Quick link builder</span> to mint a tracked redirect URL — paste a product URL or leave blank for the marketplace homepage.</li>
            <li>Send traffic to that URL. <span className="font-mono">cid</span> should be replaced per-visitor with a unique click ID (Shopnomix accepts a <span className="font-mono">{"{click_id}"}</span> macro).</li>
            <li>Conversions surface under <span className="font-medium">Amazon → Conversions</span>. Status flow: <span className="font-mono">OPEN → CONFIRMED</span> (or <span className="font-mono">REJECTED</span>). Confirmation typically takes 7–120 days.</li>
          </ol>
          <p className="text-xs">Sales/order value is <span className="font-medium">not</span> returned by the Reporting API yet — Shopnomix's dev team is working on it. Today you only see commission value + status.</p>
        </CardContent>
      </Card>
    </>
  );
}

function AmzLinkPage() {
  const [marketplace, setMarketplace] = useState("gb");
  const [productUrl, setProductUrl] = useState("");
  const [cid, setCid] = useState("");
  const [source, setSource] = useState("");
  const [link, setLink] = useState("");
  const [loading, setLoading] = useState(false);
  const isLive = ENABLED_AMZ_GEOS.has(marketplace);

  const build = async () => {
    const mp = AMAZON_MARKETPLACES.find((m) => m.code === marketplace);
    const baseUrl = productUrl.trim() || `https://${mp.host}`;
    setLoading(true);
    try {
      const params = { url: baseUrl };
      if (cid) params.cid = cid;
      if (source) params.source = source;
      const { data } = await axios.get(`${API}/redirect-link`, { params });
      setLink(data.link);
    } catch { toast.error("Failed to build link"); }
    finally { setLoading(false); }
  };

  return (
    <>
      <PageHeader title="Quick Amazon link builder" description="Mint a Shopnomix tracking redirect with your Amazon partner tag for the chosen marketplace." badge="Amazon" />
      <Card className="border-zinc-200/70">
        <CardContent className="space-y-4 py-6">
          <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900 space-y-1">
            <div><span className="font-medium">Live marketplaces:</span> GB · DE · FR. Other geos are listed but won't be attributed until Shopnomix enables them — ask Anthing.</div>
            <div><span className="font-medium">Note:</span> Amazon stats roll up at the campaign + geo level only — they can't segment by <span className="font-mono">cid</span> or <span className="font-mono">source</span>. You can leave both blank for Amazon; including them won't break anything but won't show up in reports either.</div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Marketplace</Label>
              <Select value={marketplace} onValueChange={setMarketplace}>
                <SelectTrigger data-testid="amz-quick-marketplace"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AMAZON_MARKETPLACES.map((m) => (
                    <SelectItem key={m.code} value={m.code}>
                      <span className="flex items-center gap-2">
                        {m.host}
                        {ENABLED_AMZ_GEOS.has(m.code) ? <Badge variant="default" className="h-4 text-[9px]">live</Badge> : null}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isLive ? <div className="mt-1 text-[11px] text-rose-700">Not enabled on this campaign — clicks won't be attributed.</div> : null}
            </div>
            <div className="md:col-span-2 xl:col-span-3">
              <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Product URL (optional)</Label>
              <Input value={productUrl} onChange={(e) => setProductUrl(e.target.value)} placeholder={`https://${(AMAZON_MARKETPLACES.find((m) => m.code === marketplace) || {}).host}/dp/B0XXXXXXXX`} data-testid="amz-quick-url" />
            </div>
            <div className="md:col-span-2">
              <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Click ID — cid <span className="ml-1 normal-case tracking-normal text-zinc-400">(optional for Amazon)</span></Label>
              <Input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="leave blank for Amazon" data-testid="amz-quick-cid" />
            </div>
            <div className="md:col-span-2">
              <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Sub ID — source <span className="ml-1 normal-case tracking-normal text-zinc-400">(optional for Amazon)</span></Label>
              <Input value={source} onChange={(e) => setSource(e.target.value)} placeholder="leave blank for Amazon" data-testid="amz-quick-source" />
            </div>
          </div>
          <Button onClick={build} disabled={loading} data-testid="amz-quick-build">
            <Link2 className="h-4 w-4" /> {loading ? "Generating…" : "Generate link"}
          </Button>
          {link ? <CopyableLink link={link} testId="amz-quick-link" /> : null}
        </CardContent>
      </Card>
    </>
  );
}

function AmzSearchPage() {
  const [keywords, setKeywords] = useState("");
  const [country, setCountry] = useState("us");
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!keywords.trim()) return toast("Enter keywords");
    setLoading(true); setErr(null);
    try {
      const { data } = await axios.post(`${API}/amazon/search`, {
        country_code: country, keywords: keywords.trim(), itemCount: 10,
      });
      const list = data?.SearchResult?.Items || [];
      setItems(list);
      if (list.length === 0) setErr(typeof data === "string" ? data : JSON.stringify(data));
    } catch (e) {
      setItems([]);
      const msg = e?.response?.data || e.message;
      setErr(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally { setLoading(false); }
  };

  return (
    <>
      <PageHeader title="Amazon product search" description="Live results from Amazon Creators API. Subject to a Shopnomix feature flag — if you see 'feature_disabled', ask Anthing to enable it." badge="Amazon" />
      <Card className="border-zinc-200/70">
        <CardContent className="space-y-4 py-6">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[260px]">
              <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Keywords</Label>
              <Input value={keywords} onChange={(e) => setKeywords(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder='e.g. "wireless headphones"' data-testid="amazon-keywords-input" />
            </div>
            <div className="w-[200px]">
              <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Marketplace</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="amazon-country-select"><SelectValue /></SelectTrigger>
                <SelectContent>{COUNTRY_OPTIONS.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={run} disabled={loading} data-testid="amazon-search-btn"><SearchIcon className="h-4 w-4" /> {loading ? "Searching…" : "Search"}</Button>
          </div>
          {items.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="amazon-results">
              {items.map((it, i) => {
                const title = it?.ItemInfo?.Title?.DisplayValue || "Untitled";
                const img = it?.Images?.Primary?.Large?.URL;
                const price = it?.OffersV2?.Listings?.[0]?.Price?.Money?.DisplayAmount;
                return (
                  <a key={it.ASIN || i} href={it.DetailPageURL} target="_blank" rel="noreferrer" className="group rounded-lg border border-zinc-200 bg-white p-3 transition hover:border-zinc-300 hover:shadow-sm" data-testid={`amazon-item-${i}`}>
                    <div className="flex h-32 items-center justify-center overflow-hidden rounded-md bg-zinc-50">
                      {img ? <img src={img} alt={title} className="max-h-full max-w-full object-contain" /> : <ShoppingBag className="h-10 w-10 text-zinc-300" />}
                    </div>
                    <div className="mt-3 line-clamp-2 text-sm font-medium text-zinc-800 group-hover:text-zinc-950">{title}</div>
                    <div className="mt-1 flex items-center justify-between text-xs">
                      <span className="font-mono tabular-nums text-zinc-700">{price || "—"}</span>
                      <span className="font-mono uppercase tracking-wide text-zinc-400">{it.ASIN}</span>
                    </div>
                  </a>
                );
              })}
            </div>
          ) : (
            <EmptyState title={err ? "Amazon API rejected the request" : "Search Amazon to see live products"} hint={err ? `Upstream said: ${String(err).slice(0, 240)}` : "If you get 'feature_disabled', the Amazon Catalog flag isn't enabled for this campaign."} />
          )}
        </CardContent>
      </Card>
    </>
  );
}

function AmzConvPage({ conversions, byDomain }) {
  const amzClicks = useMemo(() => byDomain.filter((d) => isAmazon(d.domain)).reduce((s, x) => s + Number(x.clicks || 0), 0), [byDomain]);
  const amzRev = useMemo(() => byDomain.filter((d) => isAmazon(d.domain)).reduce((s, x) => s + Number(x.revenue || 0), 0), [byDomain]);
  const amzRows = useMemo(() => byDomain.filter((d) => isAmazon(d.domain)), [byDomain]);
  const amzConvUnexpected = useMemo(() => conversions.filter((c) => isAmazon(c.root_domain)), [conversions]);

  return (
    <>
      <PageHeader title="Amazon performance" description="Aggregate clicks &amp; commission by Amazon TLD. Amazon doesn't share per-conversion data — only totals." badge="Amazon" />

      <Card className="mb-4 border-rose-200 bg-rose-50/40">
        <CardContent className="py-4 text-xs text-rose-900">
          <span className="font-medium">Reminder:</span> Amazon doesn't return individual conversions or status (OPEN/CONFIRMED/REJECTED) — only aggregate clicks and commission. There's also no S2S postback for Amazon, so Engagino won't receive Amazon conversion events. Use the numbers below as the source of truth.
        </CardContent>
      </Card>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Stat icon={MousePointerClick} label="Amazon clicks" value={fmtInt(amzClicks)} sub="Selected range · all live geos" testId="amz-stat-clicks" accent="amazon" />
        <Stat icon={DollarSign} label="Amazon commission" value={fmtMoney(amzRev)} sub="USD" testId="amz-stat-revenue" accent="amazon" />
      </div>

      <Card className="border-zinc-200/70">
        <CardHeader>
          <CardTitle className="text-base">Per-day breakdown by Amazon TLD</CardTitle>
          <CardDescription>From <span className="font-mono">/api/v1/reporting/domain</span> filtered to amazon.* domains</CardDescription>
        </CardHeader>
        <CardContent>
          <SimpleTable
            testId="amz-bydomain-table"
            columns={[
              { key: "date", label: "Date", mono: true, render: (r) => fmtDate(r.date) },
              { key: "domain", label: "Marketplace" },
              { key: "clicks", label: "Clicks", mono: true, render: (r) => fmtInt(r.clicks) },
              { key: "revenue", label: "Commission", mono: true, render: (r) => fmtMoney(r.revenue) },
            ]}
            rows={amzRows}
            empty={{ title: "No Amazon activity in this range yet", hint: "Check that you sent traffic with matching TLDs (amazon.co.uk for GB, amazon.de for DE, amazon.fr for FR). Stats refresh hourly." }}
          />
        </CardContent>
      </Card>

      {amzConvUnexpected.length > 0 ? (
        <Card className="mt-4 border-zinc-200/70">
          <CardHeader>
            <CardTitle className="text-base">Unexpected: per-row Amazon conversions</CardTitle>
            <CardDescription>Per Anthing, Amazon shouldn't return per-conversion rows. If these appear, double-check with Shopnomix.</CardDescription>
          </CardHeader>
          <CardContent><ConversionsTable rows={amzConvUnexpected} scope="amazon" /></CardContent>
        </Card>
      ) : null}
    </>
  );
}

function BrHowToPage() {
  return (
    <>
      <PageHeader title="How brand monetization works" description="Around 2,200 non-Amazon brands across countries. ~95% are CPA, the rest CPC backfill." badge="Brands" />
      <Card className="border-emerald-200 bg-emerald-50/40">
        <CardContent className="space-y-3 py-6 text-sm text-emerald-900">
          <p>The EPC on each catalog card is Shopnomix's prediction for your traffic — it blends both CPA and CPC performance.</p>
          <ol className="list-decimal space-y-1 pl-5">
            <li>Browse the catalog under <span className="font-medium">Brands → Catalog</span>. Filter by domain or country.</li>
            <li>Click <span className="font-medium">Use</span> on any brand to pre-fill the link builder.</li>
            <li>Generate a tracked redirect with a unique <span className="font-mono">cid</span> and a <span className="font-mono">source</span> tag for your placement.</li>
            <li>Drop the URL anywhere — site, email, social, paid ad. Shopnomix monetizes if a deal exists, otherwise falls back to your <span className="font-mono">fb</span> URL.</li>
            <li>Conversions appear under <span className="font-medium">Brands → Conversions</span>. Reporting refreshes hourly per merchant.</li>
          </ol>
        </CardContent>
      </Card>
    </>
  );
}

function BrCatalogPage({ onUseBrand }) {
  const [items, setItems] = useState([]);
  const [country, setCountry] = useState("all");
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async (reset = true, cursor = null) => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (country !== "all") params.country_codes = country;
      if (cursor) params.next = cursor;
      const { data } = await axios.get(`${API}/demand`, { params });
      const newItems = data?.data || [];
      setItems((prev) => reset ? newItems : [...prev, ...newItems]);
      const np = data?.meta?.next_page || null;
      if (np) { const u = new URL(np); setNextCursor(u.searchParams.get("next")); } else setNextCursor(null);
    } catch { toast.error("Failed to load catalog"); }
    finally { setLoading(false); }
  }, [country]);

  useEffect(() => { load(true, null); }, [load]);

  const filtered = useMemo(() => {
    if (!filter.trim()) return items;
    const q = filter.toLowerCase();
    return items.filter((b) => (b.root_domain || "").toLowerCase().includes(q));
  }, [items, filter]);

  return (
    <>
      <PageHeader title="Brand catalog" description="Eligible brands you can monetize · Amazon excluded by Shopnomix design" badge="Brands" />
      <Card className="border-zinc-200/70">
        <CardHeader className="flex flex-row flex-wrap items-end justify-between gap-3 pb-4">
          <div className="text-xs text-zinc-500">{filtered.length} of {items.length} loaded</div>
          <div className="flex flex-wrap items-center gap-2">
            <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by domain…" className="h-9 w-[220px]" data-testid="brand-filter-input" />
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="w-[180px]" data-testid="demand-country-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All countries</SelectItem>
                {COUNTRY_OPTIONS.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 && !loading ? (
            <EmptyState title="No brands match" hint="Try clearing the filter or switching country." />
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="demand-grid">
              {filtered.map((b, i) => (
                <div key={`${b.root_domain}-${b.country_code}-${i}`} className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-white p-3 transition hover:border-zinc-300 hover:shadow-sm" data-testid={`demand-item-${i}`}>
                  <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md border border-zinc-200 bg-zinc-50">
                    {b.image ? <img src={b.image} alt={b.root_domain} className="h-full w-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-zinc-900">{b.root_domain}</span>
                      {b.deeplink ? <Badge variant="secondary" className="h-5 text-[10px]">deeplink</Badge> : null}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-zinc-500">
                      <span className="uppercase">{b.country_code || "global"}</span>
                      <span>·</span>
                      <span className="font-mono tabular-nums">EPC {fmtMoney(b.epc)}</span>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => onUseBrand(b)} data-testid={`use-brand-${i}`}>Use</Button>
                  <a href={b.redirect_url} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-zinc-700"><ExternalLink className="h-4 w-4" /></a>
                </div>
              ))}
            </div>
          )}
          {nextCursor ? (
            <div className="mt-4 flex justify-center">
              <Button variant="outline" size="sm" onClick={() => load(false, nextCursor)} disabled={loading} data-testid="demand-load-more">{loading ? "Loading…" : "Load more"}</Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </>
  );
}

function BrLinkPage({ prefilledUrl, clearPrefill }) {
  const [destUrl, setDestUrl] = useState(prefilledUrl || "");
  const [cid, setCid] = useState("{click_id}");
  const [source, setSource] = useState("");
  const [fb, setFb] = useState("");
  const [link, setLink] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (prefilledUrl) { setDestUrl(prefilledUrl); setLink(""); clearPrefill && clearPrefill(); } /* eslint-disable-next-line */ }, [prefilledUrl]);

  const build = async () => {
    if (!destUrl) return toast("Pick a brand or paste a destination URL");
    setLoading(true);
    try {
      const params = { url: destUrl };
      if (cid) params.cid = cid;
      if (source) params.source = source;
      if (fb) params.fb = fb;
      const { data } = await axios.get(`${API}/redirect-link`, { params });
      setLink(data.link);
    } catch { toast.error("Failed to build link"); }
    finally { setLoading(false); }
  };

  return (
    <>
      <PageHeader title="Brand link builder" description="Generate a tracked redirect for any brand domain. Pick a brand from the catalog or type any URL." badge="Brands" />
      <Card className="border-zinc-200/70">
        <CardContent className="space-y-4 py-6">
          <div className="rounded-md border border-zinc-200 bg-zinc-50/60 p-3 text-xs text-zinc-700">
            <div><span className="font-medium text-zinc-900">cid</span> = a <span className="font-medium">unique click ID</span> per visitor (your tracker fills the <span className="font-mono">{"{click_id}"}</span> macro at request time).</div>
            <div><span className="font-medium text-zinc-900">source</span> = a <span className="font-medium">sub ID</span> for grouping traffic (placement, page, campaign segment) — not a click ID.</div>
            <div><span className="font-medium text-zinc-900">fb</span> = optional fallback URL if Shopnomix can't monetize.</div>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Destination URL</Label>
            <Input value={destUrl} onChange={(e) => setDestUrl(e.target.value)} placeholder="https://brand.com/product/123" data-testid="brand-dest-input" />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Click ID — cid</Label>
              <Input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="{click_id}" data-testid="brand-cid-input" />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Sub ID — source</Label>
              <Input value={source} onChange={(e) => setSource(e.target.value)} placeholder="newsletter / homepage / cat-shoes" data-testid="brand-source-input" />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Fallback URL</Label>
              <Input value={fb} onChange={(e) => setFb(e.target.value)} placeholder="https://your-fallback.com" data-testid="brand-fb-input" />
            </div>
          </div>
          <Button onClick={build} disabled={loading} data-testid="brand-link-build"><Link2 className="h-4 w-4" /> {loading ? "Generating…" : "Generate link"}</Button>
          {link ? <CopyableLink link={link} testId="brand-link-output" /> : null}
        </CardContent>
      </Card>
    </>
  );
}

function BrConvPage({ conversions, byDomain }) {
  const brConv = useMemo(() => conversions.filter((c) => !isAmazon(c.root_domain)), [conversions]);
  const brClicks = useMemo(() => byDomain.filter((d) => !isAmazon(d.domain)).reduce((s, x) => s + Number(x.clicks || 0), 0), [byDomain]);
  const brRev = useMemo(() => byDomain.filter((d) => !isAmazon(d.domain)).reduce((s, x) => s + Number(x.revenue || 0), 0), [byDomain]);
  return (
    <>
      <PageHeader title="Brand conversions" description="Excludes Amazon. Stats refresh hourly per merchant." badge="Brands" />
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat icon={MousePointerClick} label="Brand clicks" value={fmtInt(brClicks)} sub="Selected range" testId="brand-stat-clicks" accent="brand" />
        <Stat icon={DollarSign} label="Brand commission" value={fmtMoney(brRev)} sub="USD" testId="brand-stat-revenue" accent="brand" />
        <Stat icon={Layers} label="Conversions" value={fmtInt(brConv.length)} sub={brConv.length ? `${brConv.filter((c) => c.status === "CONFIRMED").length} confirmed` : "—"} testId="brand-stat-conversions" accent="brand" />
      </div>
      <Card className="border-zinc-200/70">
        <CardContent className="py-6"><ConversionsTable rows={brConv} scope="brand" /></CardContent>
      </Card>
    </>
  );
}

function RepConvPage({ conversions }) {
  return (
    <>
      <PageHeader title="All conversions" description="Both Amazon and other brands. Filter by status using the chips below." badge="Reports" />
      <Card className="border-zinc-200/70"><CardContent className="py-6"><ConversionsTable rows={conversions} scope="all" /></CardContent></Card>
    </>
  );
}

function RepDomainPage({ byDomain }) {
  return (
    <>
      <PageHeader title="By domain" description="Clicks and commission grouped by brand domain (per day)." badge="Reports" />
      <Card className="border-zinc-200/70"><CardContent className="py-6">
        <SimpleTable
          testId="domains-table"
          columns={[
            { key: "date", label: "Date", mono: true, render: (r) => fmtDate(r.date) },
            { key: "domain", label: "Domain", render: (r) => <span className="flex items-center gap-2">{r.domain}{isAmazon(r.domain) ? <Badge variant="secondary" className="h-4 text-[10px]">amazon</Badge> : null}</span> },
            { key: "clicks", label: "Clicks", mono: true, render: (r) => fmtInt(r.clicks) },
            { key: "revenue", label: "Commission", mono: true, render: (r) => fmtMoney(r.revenue) },
          ]}
          rows={byDomain} empty={{ title: "No domain data", hint: "Once attribution lands, breakdowns appear here." }}
        />
      </CardContent></Card>
    </>
  );
}

function RepSourcePage({ bySource }) {
  return (
    <>
      <PageHeader title="By source" description="The source/placement value you passed on each redirect." badge="Reports" />
      <Card className="border-zinc-200/70"><CardContent className="py-6">
        <SimpleTable
          testId="sources-table"
          columns={[
            { key: "date", label: "Date", mono: true, render: (r) => fmtDate(r.date) },
            { key: "source", label: "Source", render: (r) => r.source || <span className="text-zinc-400">null</span> },
            { key: "clicks", label: "Clicks", mono: true, render: (r) => fmtInt(r.clicks) },
            { key: "revenue", label: "Commission", mono: true, render: (r) => fmtMoney(r.revenue) },
          ]}
          rows={bySource} empty={{ title: "No source data", hint: "Pass a source value on each redirect to populate this." }}
        />
      </CardContent></Card>
    </>
  );
}

function RepCountryPage({ byCountry }) {
  return (
    <>
      <PageHeader title="By country" description="Aggregated from conversions." badge="Reports" />
      <Card className="border-zinc-200/70"><CardContent className="py-6">
        <SimpleTable
          testId="countries-table"
          columns={[
            { key: "country", label: "Country", render: (r) => <span className="uppercase">{r.country}</span> },
            { key: "conversions", label: "Conversions", mono: true, render: (r) => fmtInt(r.conversions) },
            { key: "revenue", label: "Commission", mono: true, render: (r) => fmtMoney(r.revenue) },
          ]}
          rows={byCountry} empty={{ title: "No country breakdown", hint: "Country splits derive from conversions." }}
        />
      </CardContent></Card>
    </>
  );
}

function IntS2SPage() {
  const corrected = "https://track.engagino.com/click?cnv_id={external_click_id}&payout={net_revenue}&t11={external_sub_id}&t12={commission_date}&t13={event_type}&t14={campaign_id}&t15={commission_id}&t16={event_id}&t17={event_date}&t18={commission_status}";
  const mapping = [
    { eng: "{external_click_id}", shp: "click_id (= cid you passed)" },
    { eng: "{net_revenue}", shp: "revenue (commission, not order value)" },
    { eng: "{external_sub_id}", shp: "source (you passed on redirect)" },
    { eng: "{commission_id}", shp: "commission_id" },
    { eng: "{commission_status}", shp: "status (OPEN / CONFIRMED / REJECTED)" },
    { eng: "{commission_date}", shp: "last_updated" },
    { eng: "{event_date}", shp: "click_time" },
    { eng: "{campaign_id}", shp: "campaign ULID" },
  ];
  return (
    <>
      <PageHeader title="Engagino S2S postback" description="The URL Shopnomix calls when conversions fire, plus how its macros map to Reporting API fields." badge="Integrations" />
      <div className="space-y-4">
        <Card className="border-amber-200 bg-amber-50/60">
          <CardContent className="py-4 text-xs text-amber-900">
            <span className="font-medium">Heads-up:</span> Amazon does <span className="font-semibold">not</span> fire S2S postbacks (Anthing, Apr 2026) — Amazon only shares aggregate clicks &amp; revenue, not per-conversion records. Treat the dashboard's <span className="font-medium">Amazon → Performance</span> page as the authoritative source for Amazon numbers; everything else (CPA brands) flows through this postback.
          </CardContent>
        </Card>
        <Card className="border-rose-200 bg-rose-50/60">
          <CardContent className="space-y-2 py-5 text-xs text-rose-900">
            <div className="flex items-center gap-2 text-sm font-medium"><AlertTriangle className="h-4 w-4" /> Issues with the URL you originally sent</div>
            <ol className="list-decimal space-y-1 pl-5">
              <li><span className="font-mono">t14</span> appears twice (<span className="font-mono">{"{campaign_id}"}</span> then <span className="font-mono">{"{commission_stats}"}</span>) — the second overwrites the first; bump the second one to <span className="font-mono">t18</span>.</li>
              <li><span className="font-mono">{"{commission_stats}"}</span> is likely a typo for <span className="font-mono">{"{commission_status}"}</span> (Shopnomix's field is named <span className="font-mono">status</span>).</li>
            </ol>
          </CardContent>
        </Card>
        <Card className="border-zinc-200/70">
          <CardHeader><CardTitle className="text-base">Corrected postback URL</CardTitle></CardHeader>
          <CardContent><CopyableLink link={corrected} testId="s2s-corrected-url" /></CardContent>
        </Card>
        <Card className="border-zinc-200/70">
          <CardHeader><CardTitle className="text-base">Macro → Reporting API field</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-hidden rounded-lg border border-zinc-200">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="text-[11px] uppercase tracking-wide text-zinc-500">Engagino macro</TableHead>
                    <TableHead className="text-[11px] uppercase tracking-wide text-zinc-500">Shopnomix field</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mapping.map((m) => (
                    <TableRow key={m.eng} className="hover:bg-zinc-50/60">
                      <TableCell className="font-mono text-xs">{m.eng}</TableCell>
                      <TableCell className="text-xs">{m.shp}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200/70">
          <CardHeader><CardTitle className="text-base">How to verify a live conversion</CardTitle></CardHeader>
          <CardContent className="text-xs text-zinc-700">
            <ol className="list-decimal space-y-1 pl-5">
              <li>Open <span className="font-medium">Reports → All conversions</span>.</li>
              <li>Note the <span className="font-mono">click_id</span>, <span className="font-mono">commission_id</span>, <span className="font-mono">source</span>, <span className="font-mono">revenue</span>.</li>
              <li>In Engagino, look for a postback where <span className="font-mono">cnv_id</span> matches that <span className="font-mono">click_id</span> and <span className="font-mono">payout</span> matches that <span className="font-mono">revenue</span>.</li>
              <li>No match in Engagino → Shopnomix isn't firing the postback. No match here → Shopnomix hasn't recorded the conversion yet.</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function IntOffersPage() {
  const [country, setCountry] = useState("all");
  const [offers, setOffers] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const params = { limit: 50 };
      if (country !== "all") params.country_codes = country;
      const { data } = await axios.get(`${API}/demand/offers`, { params });
      setOffers(data?.data || []);
      if (!data?.data) setError(JSON.stringify(data));
    } catch (e) {
      setOffers([]);
      setError(e?.response?.data ? JSON.stringify(e.response.data) : e.message);
    } finally { setLoading(false); }
  };

  return (
    <>
      <PageHeader title="Demand offers (commission detail)" description="Returns 403 unless Shopnomix has enabled this feature for the campaign." badge="Integrations" />
      <Card className="border-zinc-200/70">
        <CardHeader className="flex flex-row flex-wrap items-end justify-between gap-3 pb-4">
          <div className="text-xs text-zinc-500">/api/v2/demand/:campaign_id/offers</div>
          <div className="flex items-center gap-2">
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="w-[180px]" data-testid="offers-country-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All countries</SelectItem>
                {COUNTRY_OPTIONS.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={load} disabled={loading} data-testid="offers-load-btn">{loading ? "Loading…" : "Load offers"}</Button>
          </div>
        </CardHeader>
        <CardContent>
          {offers === null ? (
            <EmptyState title="Click Load offers to fetch" hint="" />
          ) : offers.length === 0 ? (
            <EmptyState title="No offers returned" hint={error ? `Upstream said: ${error.slice(0, 240)}` : "Try a different country."} />
          ) : (
            <SimpleTable
              testId="offers-table"
              columns={[
                { key: "brand_name", label: "Brand" },
                { key: "root_domain", label: "Domain" },
                { key: "country_code", label: "Country", render: (r) => <span className="uppercase">{r.country_code}</span> },
                { key: "commission_type", label: "Type" },
                { key: "commission", label: "Commission", mono: true, render: (r) => r.flat_commission != null ? `${r.flat_commission} ${r.currency || ""}`.trim() : r.percentage_commission != null ? `${(r.percentage_commission * 100).toFixed(2)}%` : "—" },
                { key: "historical_epc", label: "Hist. EPC", mono: true, render: (r) => fmtMoney(r.historical_epc) },
                { key: "cookie_window_days", label: "Cookie", mono: true, render: (r) => r.cookie_window_days ? `${r.cookie_window_days}d` : "—" },
              ]}
              rows={offers} empty={{ title: "No offers", hint: "" }}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
}

function IntAmzUtilPage() {
  const [country, setCountry] = useState("us");
  const [asin, setAsin] = useState("");
  const [node, setNode] = useState("");
  const [mode, setMode] = useState("get_items");
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true); setResp(null);
    try {
      let url = ""; let body = { country_code: country };
      if (mode === "get_items") {
        if (!asin) return toast("Enter an ASIN");
        url = `${API}/amazon/get-items`; body.itemIds = [asin.trim()];
      } else if (mode === "get_variations") {
        if (!asin) return toast("Enter an ASIN");
        url = `${API}/amazon/get-variations`; body.asin = asin.trim();
      } else {
        if (!node) return toast("Enter a browse node id");
        url = `${API}/amazon/get-browse-nodes`; body.browseNodeIds = [node.trim()];
      }
      const { data, status } = await axios.post(url, body, { validateStatus: () => true });
      setResp({ status, body: data });
    } catch (e) { setResp({ status: "ERR", body: String(e) }); }
    finally { setLoading(false); }
  };

  return (
    <>
      <PageHeader title="Amazon utilities" description="get_items / get_variations / get_browse_nodes — subject to the Amazon Catalog feature flag." badge="Integrations" />
      <Card className="border-zinc-200/70">
        <CardContent className="space-y-4 py-6">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-[200px]">
              <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Operation</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="amz-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="get_items">get_items (ASIN)</SelectItem>
                  <SelectItem value="get_variations">get_variations (ASIN)</SelectItem>
                  <SelectItem value="get_browse_nodes">get_browse_nodes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[180px]">
              <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Marketplace</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="amz-country-select"><SelectValue /></SelectTrigger>
                <SelectContent>{COUNTRY_OPTIONS.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {mode !== "get_browse_nodes" ? (
              <div className="flex-1 min-w-[220px]">
                <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">ASIN</Label>
                <Input value={asin} onChange={(e) => setAsin(e.target.value)} placeholder="B0D1XD1ZV3" data-testid="amz-asin-input" />
              </div>
            ) : (
              <div className="flex-1 min-w-[220px]">
                <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Browse node id</Label>
                <Input value={node} onChange={(e) => setNode(e.target.value)} placeholder="340846031" data-testid="amz-node-input" />
              </div>
            )}
            <Button onClick={run} disabled={loading} data-testid="amz-run-btn">{loading ? "Running…" : "Run"}</Button>
          </div>
          {resp ? (
            <div className="space-y-2">
              <Badge variant={Number(resp.status) >= 200 && Number(resp.status) < 300 ? "default" : "destructive"} data-testid="amz-status-badge">HTTP {resp.status}</Badge>
              <pre className="max-h-[420px] overflow-auto rounded-lg border border-zinc-200 bg-zinc-950 p-4 text-xs leading-relaxed text-emerald-100" data-testid="amz-output">
{JSON.stringify(resp.body, null, 2)}
              </pre>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </>
  );
}

function DevDebugPage() {
  const ENDPOINTS = [
    { id: "conversion", label: "v2 conversions (campaign)" },
    { id: "daily", label: "v1 daily report (campaign)" },
    { id: "domain", label: "v1 by domain (campaign)" },
    { id: "source", label: "v1 by source (campaign)" },
    { id: "client_totals", label: "v1 client totals" },
    { id: "client_daily", label: "v1 client daily" },
    { id: "client_campaign", label: "v1 client campaign" },
    { id: "client_domain", label: "v1 client domain" },
    { id: "client_conversion", label: "v2 client conversions" },
    { id: "client_campaign_date", label: "v1 client campaign by date" },
  ];
  const [endpoint, setEndpoint] = useState("conversion");
  const [s, setS] = useState(format(subDays(new Date(), 29), "yyyy-MM-dd"));
  const [e, setE] = useState(todayStr());
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true); setResp(null);
    try {
      const { data } = await axios.get(`${API}/debug/raw`, { params: { endpoint, start_date: s, end_date: e, limit: 50 } });
      setResp(data);
    } catch (err) { setResp({ status: err?.response?.status, response: err?.response?.data || String(err) }); }
    finally { setLoading(false); }
  };

  return (
    <>
      <PageHeader title="API debug" description="Hit Shopnomix endpoints directly and inspect the raw response." badge="Developer" />
      <Card className="border-zinc-200/70">
        <CardContent className="space-y-4 py-6">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-[280px]">
              <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Endpoint</Label>
              <Select value={endpoint} onValueChange={setEndpoint}>
                <SelectTrigger data-testid="debug-endpoint-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ENDPOINTS.map((x) => <SelectItem key={x.id} value={x.id}>{x.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">Start</Label>
              <Input type="date" value={s} onChange={(ev) => setS(ev.target.value)} className="w-[160px]" data-testid="debug-start" /></div>
            <div><Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-500">End</Label>
              <Input type="date" value={e} onChange={(ev) => setE(ev.target.value)} className="w-[160px]" data-testid="debug-end" /></div>
            <Button onClick={run} disabled={loading} data-testid="debug-run-btn">{loading ? "Running…" : "Run"}</Button>
          </div>
          {resp ? (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <Badge variant={resp.status >= 200 && resp.status < 300 ? "default" : "destructive"} data-testid="debug-status">HTTP {resp.status}</Badge>
                <span className="break-all font-mono text-zinc-600">{resp.request_url}</span>
              </div>
              <pre className="max-h-[420px] overflow-auto rounded-lg border border-zinc-200 bg-zinc-950 p-4 text-xs leading-relaxed text-emerald-100" data-testid="debug-output">
{JSON.stringify(resp.response, null, 2)}
              </pre>
            </div>
          ) : <EmptyState title="Pick an endpoint and click Run" hint="" />}
        </CardContent>
      </Card>
    </>
  );
}

// ---------- Sidebar layout ----------

const SECTIONS = [
  { title: "Dashboard", pages: [
    { id: "overview", label: "Overview", icon: BarChart3 },
  ] },
  { title: "Amazon", pages: [
    { id: "amz-howto", label: "How it works", icon: Info },
    { id: "amz-link", label: "Quick link builder", icon: Link2 },
    { id: "amz-search", label: "Product search", icon: SearchIcon },
    { id: "amz-conv", label: "Performance", icon: BarChart3 },
  ] },
  { title: "Brands", pages: [
    { id: "br-howto", label: "How it works", icon: Info },
    { id: "br-catalog", label: "Catalog", icon: Store },
    { id: "br-link", label: "Link builder", icon: Link2 },
    { id: "br-conv", label: "Conversions", icon: Layers },
  ] },
  { title: "Reports", pages: [
    { id: "rep-conv", label: "All conversions", icon: Layers },
    { id: "rep-domain", label: "By domain", icon: Globe2 },
    { id: "rep-source", label: "By source", icon: Tag },
    { id: "rep-country", label: "By country", icon: Globe2 },
  ] },
  { title: "Integrations", pages: [
    { id: "int-s2s", label: "S2S postback", icon: Webhook },
    { id: "int-offers", label: "Demand offers", icon: Package },
    { id: "int-amzutil", label: "Amazon utilities", icon: Wrench },
  ] },
  { title: "Developer", pages: [
    { id: "dev-debug", label: "API debug", icon: Terminal },
  ] },
  { title: "Admin", adminOnly: true, pages: [
    { id: "admin-users", label: "Users", icon: UsersIcon },
  ] },
];

function Sidebar({ activePage, setActivePage, campaign, isAdmin }) {
  const sections = SECTIONS.filter((s) => !s.adminOnly || isAdmin);
  return (
    <aside className="hidden w-64 shrink-0 border-r border-zinc-200 bg-white lg:flex lg:flex-col">
      <div className="border-b border-zinc-200 px-5 py-5">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-rose-600 text-xs font-bold text-white">R</div>
          <div>
            <div className="text-sm font-semibold tracking-tight text-zinc-900">RedClub</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Source of truth</div>
          </div>
        </div>
        <div className="mt-4 rounded-md border border-zinc-200 bg-zinc-50/60 px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Campaign</div>
          <div className="mt-0.5 truncate text-sm font-medium text-zinc-900" data-testid="campaign-name">{campaign.campaign_name || "—"}</div>
          <div className="mt-0.5 truncate font-mono text-[10px] text-zinc-500">{campaign.campaign_id || "—"}</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {sections.map((section) => (
          <div key={section.title} className="mb-5">
            <div className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{section.title}</div>
            <div className="space-y-0.5">
              {section.pages.map((p) => {
                const Icon = p.icon;
                const active = activePage === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setActivePage(p.id)}
                    data-testid={`nav-${p.id}`}
                    className={`group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition ${active ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100"}`}
                  >
                    <Icon className={`h-3.5 w-3.5 ${active ? "text-white" : "text-zinc-400 group-hover:text-zinc-600"}`} />
                    <span className="flex-1 truncate">{p.label}</span>
                    {active ? <ChevronRight className="h-3.5 w-3.5 opacity-70" /> : null}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <UserMenu />
      <div className="border-t border-zinc-200 px-4 py-2 text-[10px] text-zinc-400">
        Stats refresh hourly · cadence varies
      </div>
    </aside>
  );
}

function MobileNav({ activePage, setActivePage }) {
  const allPages = SECTIONS.flatMap((s) => s.pages.map((p) => ({ ...p, section: s.title })));
  return (
    <div className="flex items-center gap-2 border-b border-zinc-200 bg-white px-4 py-3 lg:hidden">
      <Select value={activePage} onValueChange={setActivePage}>
        <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
        <SelectContent>
          {SECTIONS.map((s) => (
            <div key={s.title}>
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-zinc-400">{s.title}</div>
              {s.pages.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
            </div>
          ))}
        </SelectContent>
      </Select>
      {/* eslint-disable-next-line */}
      {allPages.length > 0 ? null : null}
    </div>
  );
}

function TopBar({ start, end, setStart, setEnd, onApply, applying }) {
  const applyPreset = (days) => {
    const e = todayStr(); const s = format(subDays(new Date(), days - 1), "yyyy-MM-dd");
    setStart(s); setEnd(e); onApply(s, e);
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 bg-white px-6 py-3">
      <div className="text-xs text-zinc-500">Date range</div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 rounded-md border border-zinc-200 px-2 py-1">
          <CalendarIcon className="h-3.5 w-3.5 text-zinc-500" />
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-7 w-[140px] border-0 px-1 shadow-none focus-visible:ring-0" data-testid="date-start-input" />
          <span className="text-zinc-400">→</span>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-7 w-[140px] border-0 px-1 shadow-none focus-visible:ring-0" data-testid="date-end-input" />
        </div>
        <Button size="sm" onClick={() => onApply(start, end)} disabled={applying} data-testid="apply-range-btn">
          <RefreshCw className={`h-3.5 w-3.5 ${applying ? "animate-spin" : ""}`} />
          Apply
        </Button>
        <div className="flex items-center gap-1">
          {RANGE_PRESETS.map((p) => (
            <Button key={p.days} size="sm" variant="ghost" onClick={() => applyPreset(p.days)} data-testid={`preset-${p.days}`} className="h-7 px-2 text-xs text-zinc-600 hover:text-zinc-900">{p.label}</Button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- App shell ----------

function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [campaign, setCampaign] = useState({ campaign_id: "", campaign_name: "" });
  const [start, setStart] = useState(format(subDays(new Date(), 29), "yyyy-MM-dd"));
  const [end, setEnd] = useState(todayStr());
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState("overview");
  const [prefilledBrandUrl, setPrefilledBrandUrl] = useState("");

  const [totals, setTotals] = useState({ total_clicks: 0, total_revenue: 0, days_with_data: 0 });
  const [byDate, setByDate] = useState([]);
  const [byDomain, setByDomain] = useState([]);
  const [bySource, setBySource] = useState([]);
  const [byCountry, setByCountry] = useState([]);
  const [conversions, setConversions] = useState([]);

  const fetchAll = useCallback(async (s, e) => {
    setLoading(true);
    try {
      const params = { start_date: s, end_date: e };
      const [t, d, dom, src, ctry, conv] = await Promise.all([
        axios.get(`${API}/reports/totals`, { params }),
        axios.get(`${API}/reports/by-date`, { params }),
        axios.get(`${API}/reports/by-domain`, { params }),
        axios.get(`${API}/reports/by-source`, { params }),
        axios.get(`${API}/reports/by-country`, { params }),
        axios.get(`${API}/reports/conversions`, { params: { ...params, limit: 1000 } }),
      ]);
      setTotals(t.data);
      setByDate(d.data?.data || []);
      setByDomain(dom.data?.data || []);
      setBySource(src.data?.data || []);
      setByCountry(ctry.data?.data || []);
      setConversions(conv.data?.data || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Failed to load reports: ${typeof msg === "string" ? msg : JSON.stringify(msg)}`);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    axios.get(`${API}/campaign`).then((r) => setCampaign(r.data)).catch(() => {});
    fetchAll(start, end);
    // eslint-disable-next-line
  }, []);

  const onApply = (s, e) => fetchAll(s, e);
  const onUseBrand = (b) => {
    setPrefilledBrandUrl(`https://${b.root_domain}`);
    setActivePage("br-link");
  };

  const renderPage = () => {
    switch (activePage) {
      case "overview": return <OverviewPage totals={totals} conversions={conversions} byDate={byDate} campaign={campaign} />;
      case "amz-howto": return <AmzHowToPage />;
      case "amz-link": return <AmzLinkPage />;
      case "amz-search": return <AmzSearchPage />;
      case "amz-conv": return <AmzConvPage conversions={conversions} byDomain={byDomain} />;
      case "br-howto": return <BrHowToPage />;
      case "br-catalog": return <BrCatalogPage onUseBrand={onUseBrand} />;
      case "br-link": return <BrLinkPage prefilledUrl={prefilledBrandUrl} clearPrefill={() => setPrefilledBrandUrl("")} />;
      case "br-conv": return <BrConvPage conversions={conversions} byDomain={byDomain} />;
      case "rep-conv": return <RepConvPage conversions={conversions} />;
      case "rep-domain": return <RepDomainPage byDomain={byDomain} />;
      case "rep-source": return <RepSourcePage bySource={bySource} />;
      case "rep-country": return <RepCountryPage byCountry={byCountry} />;
      case "int-s2s": return <IntS2SPage />;
      case "int-offers": return <IntOffersPage />;
      case "int-amzutil": return <IntAmzUtilPage />;
      case "dev-debug": return <DevDebugPage />;
      case "admin-users": return isAdmin ? <UsersPage /> : <OverviewPage totals={totals} conversions={conversions} byDate={byDate} campaign={campaign} />;
      default: return <OverviewPage totals={totals} conversions={conversions} byDate={byDate} campaign={campaign} />;
    }
  };

  return (
    <div className="flex min-h-screen bg-zinc-50/40 text-zinc-900">
      <Toaster richColors position="top-right" />
      <Sidebar activePage={activePage} setActivePage={setActivePage} campaign={campaign} isAdmin={isAdmin} />
      <div className="flex flex-1 flex-col">
        <MobileNav activePage={activePage} setActivePage={setActivePage} />
        <TopBar start={start} end={end} setStart={setStart} setEnd={setEnd} onApply={onApply} applying={loading} />
        <main className="flex-1 overflow-y-auto px-6 py-8 lg:px-10 lg:py-10">
          <div className="mx-auto max-w-5xl">{renderPage()}</div>
        </main>
      </div>
    </div>
  );
}

function Gate() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50">
        <div className="text-sm text-zinc-500">Loading…</div>
      </div>
    );
  }
  if (!user) return <LoginPage />;
  return <Dashboard />;
}

export default function App() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}
